import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { normalizeSectionId } from '@theme/section-renderer';
import { OverflowList } from '@theme/overflow-list';
import {
  yieldToMainThread,
  getViewParameterValue,
  ResizeNotifier,
  requestIdleCallback,
  decodeFirstImage,
} from '@theme/utilities';
import { ProductSelectEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {object} Prefetch
 * @property {Promise<Response>} response - Settles once the response starts, before its body arrives.
 * @property {Promise<string>} request - The response text.
 * @property {AbortController} controller - Aborts the request.
 */

/**
 * Product page fragments already fetched, keyed by request URL. Module scope because the picker's
 * own markup is morphed away on every variant change.
 * @type {Map<string, Prefetch>}
 */
const prefetchedSections = new Map();

const PREFETCH_LIMIT = 12;

/** Prefetches in flight at once, so a pass leaves the connection to the page's own requests. */
const PREFETCH_CONCURRENCY = 2;

/** Counts passes and clears; a pass starts no further request once either supersedes it. */
let prefetchPass = 0;

const clearPrefetches = () => {
  prefetchPass += 1;
  for (const { controller } of prefetchedSections.values()) controller.abort();
  prefetchedSections.clear();
};

// A cart change alters the markup every stored fragment was rendered with, including those requested while it was written.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  clearPrefetches();
  event.promise?.then(clearPrefetches, clearPrefetches);
});

// A page restored from the back-forward cache may have missed a cart change made elsewhere.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) clearPrefetches();
});

/** Upper bound on waiting for listeners, so a hung request cannot hold the next selection. */
const SETTLE_TIMEOUT = 3000;

/** Upper bound on waiting for a selection's response to start, so a stalled one cannot hold the picker and the purchase waiting on it. */
const RESPONSE_TIMEOUT = 10000;

/**
 * Fetches a fragment at low priority and stores it, evicting the least recently used entry once the cache is full.
 * @param {string} requestUrl - The request URL.
 * @returns {Promise<string>} The request.
 */
function prefetchSection(requestUrl) {
  if (prefetchedSections.size >= PREFETCH_LIMIT) {
    const leastRecentlyUsed = prefetchedSections.keys().next().value;
    if (leastRecentlyUsed) {
      prefetchedSections.get(leastRecentlyUsed)?.controller.abort();
      prefetchedSections.delete(leastRecentlyUsed);
    }
  }

  const controller = new AbortController();
  const response = fetch(requestUrl, { signal: controller.signal, priority: 'low' });
  const request = response
    .then((response) => {
      if (!response.ok) throw new Error(`Prefetch failed with ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      // A clear may already have let a newer request take this key
      if (prefetchedSections.get(requestUrl)?.request === request) prefetchedSections.delete(requestUrl);
      throw error;
    });

  // Nothing awaits a prefetch that fails before its variant is ever picked.
  request.catch(() => {});
  prefetchedSections.set(requestUrl, { response, request, controller });

  return request;
}

/**
 * Moves a prefetch to the end, so a pass that still reaches it spares it from eviction.
 * @param {string} requestUrl - The request URL.
 * @returns {boolean} Whether the prefetch is stored.
 */
function refreshPrefetch(requestUrl) {
  const entry = prefetchedSections.get(requestUrl);
  if (!entry) return false;

  prefetchedSections.delete(requestUrl);
  prefetchedSections.set(requestUrl, entry);

  return true;
}

/**
 * Removes a prefetch and returns it, so a fragment serves one click and the next pass fetches it fresh.
 * @param {string} requestUrl - The request URL.
 * @returns {Prefetch | undefined} The stored prefetch, if there is one.
 */
function takePrefetch(requestUrl) {
  const entry = prefetchedSections.get(requestUrl);
  prefetchedSections.delete(requestUrl);

  return entry;
}

/**
 * Whether the visitor asked to save data or is on a connection too slow to spend on guesses.
 * @returns {boolean}
 */
function isSavingData() {
  const connection = /** @type {{ saveData?: boolean, effectiveType?: string } | undefined} */ (
    /** @type {any} */ (navigator).connection
  );

  return Boolean(connection?.saveData) || connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g';
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** True from a selection until every listener that registered work has settled. */
  #updateInFlight = false;

  /** @type {string | null} The option value id selected while an update was in flight. */
  #queuedOptionValueId = null;

  /** Counts requests, so only the latest one releases the picker. */
  #updateGeneration = 0;

  /** @type {{ promise: Promise<boolean>, resolve: (isApplied: boolean) => void } | null} */
  #settled = null;

  /** @type {{ optionValueIds: string[], url: string }} The selection the page shows, restored when a newer one fails. */
  #appliedSelection = { optionValueIds: [], url: '' };

  /** The address the selection in flight writes. */
  #requestedUrl = '';

  /**
   * Whether a selection is still being applied, counting one queued behind the update in flight.
   * @returns {boolean}
   */
  get isPending() {
    return this.#updateInFlight || this.#queuedOptionValueId !== null;
  }

  /**
   * Resolves once no selection is pending: true when the last one was applied, false when its request failed.
   * @returns {Promise<boolean>}
   */
  whenSettled() {
    return this.#settled?.promise ?? Promise.resolve(true);
  }

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.#appliedSelection = { optionValueIds: this.selectedOptionsValues, url: window.location.href };

    this.addEventListener('change', this.variantChanged.bind(this));
    // Pointer events cover touch too: a tap fires pointerover before its click.
    this.addEventListener('pointerover', this.#prefetchSiblingOnIntent);
    this.addEventListener('focusin', this.#prefetchSiblingOnIntent);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#prefetchAfterCartChange);
    this.#resizeObserver.observe(this);
    this.#schedulePrefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#prefetchAfterCartChange);
    this.#resizeObserver.disconnect();
  }

  /**
   * Refills the prefetch cache once a cart change has landed, since the change emptied it.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event - The cart lines update event.
   */
  #prefetchAfterCartChange = (event) => {
    const schedulePrefetch = () => this.#schedulePrefetch();
    event.promise?.then(schedulePrefetch, schedulePrefetch);
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.#select(event.target);
  }

  /**
   * Applies a selection, or holds it until the update in flight has settled.
   * @param {HTMLElement} selectedOption - The changed input.
   */
  #select(selectedOption) {
    this.updateSelectedOption(selectedOption);

    if (this.#updateInFlight) {
      this.#queuedOptionValueId = selectedOption.dataset.optionValueId ?? null;
      return;
    }

    const isOnProductPage = this.#isOnProductPage();

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = this.#loadsNewProduct(selectedOption);
    const morphElementSelector = loadsNewProduct ? 'main' : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      const sellingPlanId = this.#sellingPlanId();
      if (sellingPlanId && !loadsNewProduct) {
        url.searchParams.set('selling_plan', sellingPlanId);
      } else {
        url.searchParams.delete('selling_plan');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    this.#requestedUrl = url.href;

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Re-selects the option queued during the last update. The morph restored the server's
   * checked state, so the selection runs again from the input rather than from a stored value.
   */
  #drainQueuedSelection() {
    const optionValueId = this.#queuedOptionValueId;
    this.#queuedOptionValueId = null;
    if (!optionValueId) return;

    // A combined listing sibling may not carry the option that was queued on the previous product.
    const option = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
    if (option instanceof HTMLInputElement) this.#select(option);
  }

  /**
   * Releases the next selection once the picker's own update and every registered listener have settled.
   * @param {Promise<unknown>[]} listenerWork - Work handed back through `event.waitUntil`.
   * @param {number} generation - The request this settle belongs to.
   * @param {boolean} isApplied - Whether the request's response was applied.
   */
  async #settle(listenerWork, generation, isApplied) {
    await Promise.race([
      Promise.allSettled(listenerWork),
      new Promise((resolve) => setTimeout(resolve, SETTLE_TIMEOUT)),
    ]);

    // A card swatch requests without queueing, so a newer request may already be in flight
    if (generation !== this.#updateGeneration) return;

    this.#updateInFlight = false;
    // A failed combined listing request would otherwise send the next selection to that sibling
    if (!isApplied) this.#pendingRequestUrl = undefined;
    this.#drainQueuedSelection();
    if (this.#updateInFlight) return;

    // A product card has already pointed its links at the failed selection, so it keeps showing that one
    if (!isApplied && !this.closest('product-card')) this.#restoreAppliedSelection();

    this.#settled?.resolve(isApplied);
    this.#settled = null;
  }

  /**
   * Puts back the selection the rest of the page still shows, after the request for a newer one failed.
   */
  async #restoreAppliedSelection() {
    const { optionValueIds, url } = this.#appliedSelection;

    for (const optionValueId of optionValueIds) {
      const option = this.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (option instanceof HTMLInputElement) this.updateSelectedOption(option);
    }

    if (!this.#isOnProductPage()) return;

    // The failed selection writes its address after the same yield, so this write lands after it
    await yieldToMainThread();
    if (this.isPending || !url || url === window.location.href) return;

    history.replaceState({}, '', url);
  }

  /**
   * Whether this picker drives a full product page, rather than a card or quick-add dialog.
   * @returns {boolean}
   */
  #isOnProductPage() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  /**
   * The section id to request a fragment of, when this picker drives a full product page.
   * @returns {string | null}
   */
  #productPageSectionId() {
    if (!this.#isOnProductPage()) return null;

    return normalizeSectionId(this.closest('.shopify-section')?.id ?? '') || null;
  }

  /**
   * The selling plan the product form carries, read from this picker's section.
   * @returns {string}
   */
  #sellingPlanId() {
    const input = this.closest('.shopify-section')?.querySelector('input[name="selling_plan"]');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /**
   * Builds a product page request URL. The click and the prefetch must produce byte-identical
   * URLs, since the cache is keyed on the string.
   * @param {string} productUrl - The product URL.
   * @param {string | null} sectionId - The section id to request, or null for a combined listing sibling's whole page.
   * @param {string[]} optionValues - The option value ids of the selection.
   * @param {string | undefined} variantId - The resolved variant the option selects.
   * @param {string} sellingPlanId - The selling plan to price the fragment on.
   * @returns {string} The request URL.
   */
  #productPageRequestUrl(productUrl, sectionId, optionValues, variantId, sellingPlanId) {
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (variantId) {
      params.push(`variant=${variantId}`);
    } else if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    if (sellingPlanId) params.push(`selling_plan=${sellingPlanId}`);
    if (sectionId) params.unshift(`section_id=${sectionId}`);

    return `${productUrl.split('?')[0]}?${params.join('&')}`;
  }

  /**
   * Prefetches the combined listing sibling an option is about to load, then warms its first gallery
   * image, so the click neither waits on the page nor on the image decode.
   * @param {Event} event - The pointerover or focusin event.
   */
  #prefetchSiblingOnIntent = (event) => {
    const option =
      event.target instanceof Element ? event.target.closest('label')?.querySelector('input[data-option-value-id]') : null;
    if (!(option instanceof HTMLInputElement) || option.checked || !this.#loadsNewProduct(option)) return;
    if (isSavingData()) return;

    const { connectedProductUrl = '', optionValueId = '', variantId, fieldsetIndex = '' } = option.dataset;
    const optionValues = [...this.selectedOptionsValues];
    optionValues[Number.parseInt(fieldsetIndex)] = optionValueId;

    const requestUrl = this.#productPageRequestUrl(connectedProductUrl, null, optionValues, variantId, '');
    if (prefetchedSections.has(requestUrl)) return;

    prefetchSection(requestUrl)
      .then((responseText) =>
        decodeFirstImage(new DOMParser().parseFromString(responseText, 'text/html').querySelector('media-gallery'))
      )
      .catch(() => {});
  };

  /**
   * Queues a prefetch of every selection one click away from the current one.
   */
  #schedulePrefetch() {
    if (!this.#isOnProductPage() || isSavingData()) return;

    requestIdleCallback(() => this.#prefetchReachableSections());
  }

  /**
   * Fetches the fragment for each selection the next click can produce, warming the cache.
   * Combined listing siblings are skipped: they load another product and morph `<main>`.
   */
  #prefetchReachableSections() {
    const sectionId = this.#productPageSectionId();
    const productUrl = this.dataset.productUrl;

    if (!sectionId || !productUrl) return;

    const groups = Array.from(this.querySelectorAll('fieldset'));
    const currentValues = this.selectedOptionsValues;

    if (groups.length !== currentValues.length) return;

    const requestUrlsByGroup = groups.map((group, groupIndex) => {
      /** @type {string[]} */
      const requestUrls = [];

      for (const option of group.querySelectorAll('input[data-option-value-id]')) {
        const { optionValueId, connectedProductUrl, variantId } = /** @type {HTMLElement} */ (option).dataset;

        if (!optionValueId || optionValueId === currentValues[groupIndex]) continue;
        if (connectedProductUrl && connectedProductUrl !== productUrl.split('?')[0]) continue;

        const optionValues = [...currentValues];
        optionValues[groupIndex] = optionValueId;

        // A variant click drops the selling plan before it requests, so the fragment it asks for carries none.
        requestUrls.push(this.#productPageRequestUrl(productUrl, sectionId, optionValues, variantId, ''));
      }

      return requestUrls;
    });

    // Taken in turns across the groups, so the cap leaves none of them without a prefetch.
    /** @type {string[]} */
    const requestUrls = [];
    const longestGroup = Math.max(0, ...requestUrlsByGroup.map((urls) => urls.length));
    for (let index = 0; index < longestGroup; index++) {
      for (const urls of requestUrlsByGroup) {
        const requestUrl = urls[index];
        if (requestUrl) requestUrls.push(requestUrl);
      }
    }

    // One pass never outgrows the cache, or its later entries would evict its earlier ones.
    const pending = requestUrls.slice(0, PREFETCH_LIMIT).filter((requestUrl) => !refreshPrefetch(requestUrl));
    const pass = ++prefetchPass;

    const prefetchNext = () => {
      const requestUrl = pending.shift();
      if (!requestUrl || pass !== prefetchPass) return;

      prefetchSection(requestUrl).then(prefetchNext, prefetchNext);
    };

    for (let slot = 0; slot < PREFETCH_CONCURRENCY; slot++) prefetchNext();
  }

  /**
   * Whether the selection points at a different product, as combined listing siblings do.
   * @param {HTMLElement} selectedOption - The selected option.
   * @returns {boolean}
   */
  #loadsNewProduct(selectedOption) {
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;

    return this.#isOnProductPage() && !!newUrl && newUrl !== currentUrl;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        // Re-selecting the current option leaves its checked history as it is.
        if (radios && checkedIndices && fieldset && checkedIndices[0] !== inputIndex) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // A tile names the variant it lands on, so the request asks for it by id. Option values
    // would let Shopify pair them with the current selection, published or not.
    if (selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // The product page holds everything the listeners read inside its own section, so request
    // that fragment instead of the whole document. Combined listings are the exception: they
    // morph `<main>`, and the sibling product's section id is not this one.
    const loadsNewProduct = this.#loadsNewProduct(selectedOption);
    const productPageSectionId = loadsNewProduct ? null : this.#productPageSectionId();

    if ((productPageSectionId || loadsNewProduct) && productUrl) {
      return this.#productPageRequestUrl(
        productUrl,
        productPageSectionId,
        this.selectedOptionsValues,
        selectedOption.dataset.variantId,
        loadsNewProduct ? '' : this.#sellingPlanId()
      );
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.#updateInFlight = true;
    const generation = ++this.#updateGeneration;

    if (!this.#settled) {
      /** @type {(isApplied: boolean) => void} */
      let resolveSettled = () => {};
      const promise = new Promise((resolve) => {
        resolveSettled = resolve;
      });
      this.#settled = { promise, resolve: resolveSettled };
    }

    const deferredEventPromise = ProductSelectEvent.createPromise();
    // Rejected on a failed fetch; listeners need not each attach a handler.
    deferredEventPromise.promise.catch(() => {});
    const selectedOptions = this.getAllSelectedOptions();

    /** @type {Promise<unknown>[]} */
    const listenerWork = [];
    const event = new ProductSelectEvent({
      product: {
        id: this.dataset.productId ?? '',
        title: this.dataset.productTitle ?? '',
        handle: this.dataset.productHandle ?? '',
      },
      selectedOptions,
      detail: {
        optionValueId,
        variantId,
        connectedProductUrl,
      },
      promise: deferredEventPromise.promise,
    });

    // A listener whose work outlives the response registers it here; the next selection waits for it.
    event.waitUntil = (work) => {
      listenerWork.push(work);
    };
    this.dispatchEvent(event);

    const { signal } = abortController;
    /** @type {number | undefined} */
    let responseTimeout;
    const startResponseTimeout = () => {
      clearTimeout(responseTimeout);
      // Not an AbortError: listeners take that for a newer selection that will follow, and none does here
      responseTimeout = setTimeout(
        () => abortController.abort(new DOMException('The selection request timed out', 'TimeoutError')),
        RESPONSE_TIMEOUT
      );
    };
    const fetchSection = () => {
      startResponseTimeout();
      return fetch(requestUrl, { signal }).then((response) => {
        clearTimeout(responseTimeout);
        if (!response.ok) throw new Error(`Section request failed with ${response.status}`);
        return response.text();
      });
    };

    // A prefetched response carries no signal, so the abort races it; one that failed is requested again.
    const prefetched = takePrefetch(requestUrl);
    if (prefetched) {
      startResponseTimeout();
      prefetched.response.then(() => clearTimeout(responseTimeout), () => {});
    }
    /** @type {Promise<never>} */
    const whenAborted = new Promise((_, reject) => {
      signal.addEventListener('abort', () => reject(signal.reason), { once: true });
    });
    const responsePromise = Promise.race([
      prefetched ? prefetched.request.catch(fetchSection) : fetchSection(),
      whenAborted,
    ]);
    let isApplied = false;

    responsePromise
      .then(async (responseText) => {
        clearTimeout(responseTimeout);
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
              morphElementSelector,
            },
          });
          return;
        }

        let newProduct;

        // A morph replaces the gallery outright, so its first image decodes before the page swaps.
        if (morphElementSelector) {
          await decodeFirstImage(html.querySelector('media-gallery'));
          signal.throwIfAborted();
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Only now do the product URL, the option inputs and the section id belong to the
        // selection that was just made — a combined listing sibling swaps all three.
        this.#schedulePrefetch();

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
                morphElementSelector,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
            morphElementSelector,
          },
        });
      })
      .then(() => {
        isApplied = true;
        this.#appliedSelection = {
          optionValueIds: this.selectedOptionsValues,
          url: this.#requestedUrl || window.location.href,
        };
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        clearTimeout(responseTimeout);
        this.#settle(listenerWork, generation, isApplied);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement)) return undefined;

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
